import React, { useEffect, useState, useRef, useCallback } from "react";
import { io, Socket } from "socket.io-client";
import deliveryAgentsApi from "../../api/delivery-agents";
import Layout from "../../components/layout/Layout";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Avatar,
  Divider,
  Paper,
  ListItemButton,
} from "@mui/material";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";

const MAP_CENTER = { lat: 12.9121, lng: 77.6446 }; // HSR Layout, Bangalore
const containerStyle = {
  width: "100%",
  height: "calc(100vh - 180px)",
};

interface RiderLocation {
  lat: number;
  lng: number;
  accuracy?: number;
  updatedAt?: string;
}

interface Rider {
  _id: string;
  name: string;
  phone: string;
  phoneNumber: string;
  lastLocation: RiderLocation | null;
  activeOrderId: string | null;
  activeOrderNumber: string | null;
  isOffline?: boolean;
}

const LiveRiderMap: React.FC = () => {
  const [riders, setRiders] = useState<Rider[]>([]);
  const [selectedRider, setSelectedRider] = useState<Rider | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Record<string, google.maps.Marker>>({});
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  // Script Loader: check global google maps instance to avoid double injection collisions
  useEffect(() => {
    if (window.google?.maps) {
      setIsMapLoaded(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"], script[src*="maps.google.com/maps/api/js"]'
    );

    const checkInterval = setInterval(() => {
      if (window.google?.maps) {
        setIsMapLoaded(true);
        clearInterval(checkInterval);
      }
    }, 100);

    if (existingScript) {
      const handleLoad = () => {
        setIsMapLoaded(true);
        clearInterval(checkInterval);
      };
      existingScript.addEventListener("load", handleLoad);
      return () => {
        existingScript.removeEventListener("load", handleLoad);
        clearInterval(checkInterval);
      };
    }

    const script = document.createElement("script");
    const key = process.env.REACT_APP_GOOGLE_MAPS_KEY || "AIzaSyCwFYHOFKgcgkXqX2z33ryuhgPOYhCNoEY";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsMapLoaded(true);
      clearInterval(checkInterval);
    };
    script.onerror = () => {
      clearInterval(checkInterval);
    };
    document.head.appendChild(script);

    return () => {
      clearInterval(checkInterval);
    };
  }, []);

  const fetchRiders = async () => {
    try {
      const data = await deliveryAgentsApi.getLiveRiders();
      if (data.success) {
        setRiders(data.riders);
      }
    } catch (err) {
      console.error("Failed to fetch live riders:", err);
    } finally {
      setLoading(false);
    }
  };

  // Socket setup
  useEffect(() => {
    fetchRiders();

    const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
    const socket = io(API_URL, {
      auth: {
        token: localStorage.getItem("adminToken"),
      },
      transports: ["websocket"],
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Admin connected to Socket.IO live track");
      socket.emit("admin:join");
    });

    socket.on("rider:location", (data: { riderId: string; lat: number; lng: number; accuracy: number; orderId: string | null; timestamp: string }) => {
      setRiders((prevRiders) => {
        return prevRiders.map((r) => {
          if (r._id === data.riderId) {
            return {
              ...r,
              isOffline: false,
              activeOrderId: data.orderId,
              lastLocation: {
                lat: Number(data.lat),
                lng: Number(data.lng),
                accuracy: data.accuracy,
                updatedAt: data.timestamp,
              },
            };
          }
          return r;
        });
      });
    });

    socket.on("rider:status", (data: { riderId: string; isOnline: boolean }) => {
      if (data.isOnline) {
        fetchRiders();
      } else {
        setRiders((prevRiders) =>
          prevRiders.map((r) =>
            r._id === data.riderId ? { ...r, isOffline: true } : r
          )
        );
      }
    });

    socket.on("rider:offline", (data: { riderId: string }) => {
      setRiders((prevRiders) =>
        prevRiders.map((r) =>
          r._id === data.riderId ? { ...r, isOffline: true } : r
        )
      );
    });

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  const syncMarkers = useCallback(() => {
    if (!mapInstanceRef.current) return;

    const activeRiderIds = new Set<string>();

    riders.forEach((rider) => {
      if (!rider.lastLocation || rider.isOffline) {
        // Remove marker if offline or location is missing
        if (markersRef.current[rider._id]) {
          markersRef.current[rider._id].setMap(null);
          delete markersRef.current[rider._id];
        }
        return;
      }

      const lat = Number(rider.lastLocation.lat);
      const lng = Number(rider.lastLocation.lng);
      if (isNaN(lat) || isNaN(lng)) return;

      activeRiderIds.add(rider._id);
      const position = { lat, lng };

      const iconUrl = rider.activeOrderId
        ? "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
        : "https://maps.google.com/mapfiles/ms/icons/orange-dot.png";

      if (markersRef.current[rider._id]) {
        // Update existing marker
        markersRef.current[rider._id].setPosition(position);
        markersRef.current[rider._id].setIcon(iconUrl);
      } else {
        // Create new marker
        const marker = new google.maps.Marker({
          position,
          map: mapInstanceRef.current!,
          title: rider.name,
          icon: iconUrl,
        });

        marker.addListener("click", () => {
          handleRiderSelect(rider);
        });

        markersRef.current[rider._id] = marker;
      }
    });

    // Cleanup disconnected markers
    Object.keys(markersRef.current).forEach((riderId) => {
      if (!activeRiderIds.has(riderId)) {
        markersRef.current[riderId].setMap(null);
        delete markersRef.current[riderId];
      }
    });
  }, [riders]);

  // Callback ref to initialize map when the container element mounts in the DOM
  const mapContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null && isMapLoaded && !mapInstanceRef.current) {
      try {
        mapInstanceRef.current = new google.maps.Map(node, {
          center: MAP_CENTER,
          zoom: 13,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
        });

        infoWindowRef.current = new google.maps.InfoWindow();

        infoWindowRef.current.addListener("closeclick", () => {
          setSelectedRider(null);
        });

        // Trigger sync of markers on initialization
        syncMarkers();
      } catch (e) {
        console.error("Error initializing Google Map:", e);
      }
    }
  }, [isMapLoaded, syncMarkers]);

  // Sync state riders to Google Map markers
  useEffect(() => {
    syncMarkers();
  }, [riders, isMapLoaded, syncMarkers]);

  const handleRiderSelect = (rider: Rider) => {
    setSelectedRider(rider);
    if (!mapInstanceRef.current || !rider.lastLocation) return;

    const lat = Number(rider.lastLocation.lat);
    const lng = Number(rider.lastLocation.lng);
    if (isNaN(lat) || isNaN(lng)) return;

    const position = { lat, lng };

    mapInstanceRef.current.panTo(position);
    mapInstanceRef.current.setZoom(15);

    if (infoWindowRef.current) {
      const contentString = `
        <div style="padding: 6px; min-width: 180px; font-family: 'Plus Jakarta Sans', sans-serif;">
          <h4 style="margin: 0 0 4px; font-size: 13px; font-weight: 700; color: #0f172a;">${rider.name}</h4>
          <p style="margin: 0 0 6px; font-size: 11px; color: #64748b;">Phone: ${rider.phone || rider.phoneNumber}</p>
          <div style="border-top: 1px solid #e2e8f0; padding-top: 6px; margin-top: 6px; font-size: 11px;">
            <strong>Status:</strong> ${rider.activeOrderId ? "Delivering" : "Idle (Online)"}
            ${rider.activeOrderNumber ? `<div style="margin-top: 4px; font-weight: 600;">Active Order: ${rider.activeOrderNumber}</div>` : ""}
          </div>
        </div>
      `;

      infoWindowRef.current.setContent(contentString);

      const marker = markersRef.current[rider._id];
      if (marker) {
        infoWindowRef.current.open(mapInstanceRef.current, marker);
      } else {
        infoWindowRef.current.setPosition(position);
        infoWindowRef.current.open(mapInstanceRef.current);
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
          <CircularProgress color="primary" />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
              Live Rider Map
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor all active delivery agents on the field in real-time
            </Typography>
          </Box>
          <Chip
            icon={<RefreshIcon />}
            label="Reload All"
            onClick={fetchRiders}
            variant="outlined"
            color="primary"
            sx={{ cursor: "pointer", fontWeight: 600 }}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 9 }}>
            <Paper elevation={0} sx={{ p: 0.5, borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", position: "relative" }}>
              <div ref={mapContainerRef} style={containerStyle} />
              {!isMapLoaded && (
                <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "rgba(255,255,255,0.7)" }}>
                  <CircularProgress color="primary" />
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Card elevation={0} sx={{ height: "calc(100vh - 170px)", border: "1px solid #e2e8f0", borderRadius: 3, display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#0f172a" }}>
                  Active Riders ({riders.filter((r) => !r.isOffline).length})
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                  {riders.filter((r) => !r.isOffline).length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 8 }}>
                      <HelpOutlineIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No online riders currently
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ p: 0 }}>
                      {riders
                        .filter((r) => !r.isOffline)
                        .map((rider) => (
                          <ListItem
                            key={rider._id}
                            disablePadding
                            sx={{ mb: 1 }}
                          >
                            <ListItemButton
                              onClick={() => {
                                if (rider.lastLocation) {
                                  handleRiderSelect(rider);
                                }
                              }}
                              sx={{
                                borderRadius: 2,
                                border: selectedRider?._id === rider._id ? "1.5px solid #f97316" : "1px solid #e2e8f0",
                                backgroundColor: selectedRider?._id === rider._id ? "rgba(249, 115, 22, 0.04)" : "transparent",
                                "&:hover": {
                                  backgroundColor: "rgba(249, 115, 22, 0.02)",
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                <Avatar sx={{ bgcolor: rider.activeOrderId ? "#f97316" : "#22c55e", width: 32, height: 32 }}>
                                  {rider.activeOrderId ? <LocalShippingIcon sx={{ fontSize: 18 }} /> : <PersonPinCircleIcon sx={{ fontSize: 18 }} />}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                                    {rider.name}
                                  </Typography>
                                }
                                secondary={
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {rider.phoneNumber}
                                    </Typography>
                                    {rider.activeOrderNumber ? (
                                      <Chip
                                        label={rider.activeOrderNumber}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, mt: 0.5 }}
                                      />
                                    ) : (
                                      <Chip
                                        label="Idle"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, mt: 0.5 }}
                                      />
                                    )}
                                  </Box>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                    </List>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default LiveRiderMap;
